// web-search-browser.ts — WEB-SURFACE web search. The browser half of Pattern 7.
//
// THE PRINCIPLE IS UNCHANGED: retrieval happens on the CUSTOMER'S side, never Oriora's. On native
// that is the device (search.rs, hidden webview). Here it is the customer's BROWSER, calling from
// THEIR IP. Hundreds of customers are hundreds of browsers — nothing funnels through our server, so
// there is no shared bucket to rate-limit. Oriora holds no search key and runs no search. Ever.
//
// Why this file has to exist at all: a web page is forbidden by the browser from reading another
// site's response unless that site opts in with Access-Control-Allow-Origin. The native app is not
// a browser, so it is not bound by that. Before this, the web build simply threw and the research
// loop fell back to the vendor-native path — which is wired for ONE vendor, so a web customer on
// any other key got an answer presented as sourced with nothing actually retrieved.
//
// TWO TIERS, measured live 2026-08-14 (see champion-lifecycle/WEB-SEARCH-FEATURE.md):
//
//   TIER 1 — sources that let the browser read them DIRECTLY. Wikipedia, OpenAlex, Crossref,
//   Europe PMC, Stack Exchange, Hacker News. No intermediary at all: the customer's browser talks
//   straight to the source, so nobody in the middle learns what they are researching. Preferred.
//
//   TIER 2 — a keyless reader for the open web, for everything Tier 1 cannot reach (news, blogs,
//   vendor pages). ⚠️ The reader sees the URL being read. That is a real privacy cost and the app
//   must say so — it is not hidden here.
//
// What is still BANNED, unchanged: Oriora running the search on its own server/key. That is the one
// design that funnels every customer through one IP, and it is why this file uses neither.
import { WIKI_HOSTS, tierOneSearch, type WebResult } from "./web-search-tier1";

export type { WebResult };

/** Readers, in order. Two independent services so one going away is not an outage. */
const READERS = [
  (url: string) => `https://r.jina.ai/${url}`,
  (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
];

async function readVia(url: string, signal?: AbortSignal): Promise<string> {
  for (const make of READERS) {
    try {
      const r = await fetch(make(url), { signal });
      if (!r.ok) continue;
      const text = await r.text();
      if (text.trim()) return text;
    } catch {
      /* try the next reader */
    }
  }
  return "";
}

/** DuckDuckGo's results page, read through a reader, parsed into results.
 *  r.jina.ai returns markdown (`## [Title](url)`); allorigins returns raw HTML (`result__a`).
 *  Both shapes are handled so either reader can serve. */
function parseResults(body: string, limit: number): WebResult[] {
  const out: WebResult[] = [];
  const seen = new Set<string>();
  const push = (title: string, url: string, snippet: string) => {
    // DuckDuckGo wraps outbound links; unwrap so the customer gets the real source.
    let clean = url;
    const m = /[?&]uddg=([^&]+)/.exec(url);
    if (m) { try { clean = decodeURIComponent(m[1]); } catch { /* keep as-is */ } }
    if (!/^https?:\/\//i.test(clean)) return;
    // Never present the search engine or an ad redirector as a source.
    if (/duckduckgo\.com|bing\.com\/aclick|\.js\?/i.test(clean)) return;
    if (seen.has(clean) || out.length >= limit) return;
    seen.add(clean);
    out.push({ title: title.trim().slice(0, 300) || clean, url: clean, snippet: snippet.trim().slice(0, 600) });
  };

  // markdown shape
  for (const m of body.matchAll(/^#{1,4}\s*\[([^\]]+)\]\(([^)]+)\)/gm)) push(m[1], m[2], "");
  // html shape
  if (!out.length) {
    for (const m of body.matchAll(/class="result__a"[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/g)) {
      push(m[2].replace(/<[^>]+>/g, ""), m[1].startsWith("//") ? "https:" + m[1] : m[1], "");
    }
  }
  return out;
}

/**
 * Find results for `query` from the customer's browser.
 * Tier 1 first (no intermediary), then the open web via a reader.
 */
export async function browserSearch(query: string, limit = 5, signal?: AbortSignal): Promise<WebResult[]> {
  const q = query.trim();
  if (!q) return [];

  const direct = await tierOneSearch(q, limit, signal);
  if (direct.length >= limit) return direct.slice(0, limit);

  const body = await readVia(`https://html.duckduckgo.com/html/?q=${encodeURIComponent(q)}`, signal);
  const open = parseResults(body, limit);

  // Tier 1 hits lead — they are the ones with no middleman and the better provenance.
  const merged = [...direct];
  for (const r of open) {
    if (merged.length >= limit) break;
    if (!merged.some((m) => m.url === r.url)) merged.push(r);
  }
  return merged;
}

/**
 * Read one page from the customer's browser.
 * Wikipedia (and friends) are fetched DIRECTLY — no reader, no third party. Everything else goes
 * through a reader, because the browser is not allowed to read it otherwise.
 */
export async function browserFetch(url: string, maxChars = 6000, signal?: AbortSignal): Promise<string> {
  if (!/^https?:\/\//i.test(url)) return "";

  // Tier 1: direct, when the source allows it.
  try {
    const host = new URL(url).hostname;
    if (WIKI_HOSTS.test(host)) {
      const title = decodeURIComponent(url.split("/wiki/")[1] ?? "");
      if (title) {
        const api = `https://${host}/w/api.php?action=query&prop=extracts&explaintext=1&redirects=1&format=json&origin=*&titles=${encodeURIComponent(title)}`;
        const r = await fetch(api, { signal });
        if (r.ok) {
          const j = (await r.json()) as { query?: { pages?: Record<string, { extract?: string }> } };
          const page = Object.values(j.query?.pages ?? {})[0];
          if (page?.extract) return page.extract.slice(0, maxChars);
        }
      }
    }
  } catch {
    /* fall through to the reader */
  }

  const body = await readVia(url, signal);
  if (!body) return "";
  // r.jina.ai returns markdown with a small header block; drop it so the model reads the page.
  const cut = body.indexOf("Markdown Content:");
  const text = cut >= 0 ? body.slice(cut + "Markdown Content:".length) : body;
  return text.replace(/\s+\n/g, "\n").trim().slice(0, maxChars);
}
